import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import DateSelector from '../Common/DateSelector';
import './CoverageAnalysisView.css';

interface Dealer {
  dealer_id: string;
  dealer_name: string;
  spend?: number | null;
  ecpm?: number | null;
  projected_store_visits?: number | null;
  fta_dampened?: boolean | null;
  fta_raw_projected?: number | null;
  fta_anomaly_flags?: string[] | null;
  priority_level: 'High' | 'Medium' | 'Low';
  visibility_status: 'zero' | 'critical' | 'low' | 'fair' | 'good';
  channel?: string;
  impressions: number;
  channel_visibility?: {
    [channel: string]: { impressions: number; status: string; };
  };
  city: string;
  state: string;
  district_manager: string;
  regional_manager: string;
  last_analyzed: string;
}

const CHANNEL_COLUMNS = [
  'Display', 'DOOH', 'Streaming TV', 'Streaming Audio', 'Broadcast TV',
  'Radio', 'Paid Social', 'Sport Signage'
];

interface Summary {
  total_matching: number;
  by_priority: {
    High: number;
    Medium: number;
    Low: number;
  };
  by_visibility: {
    zero: number;
    critical: number;
    low: number;
    fair: number;
    good: number;
  };
}

interface AdvancedCoverageData {
  success: boolean;
  dealers: Dealer[];
  summary: Summary;
  filters_applied: {
    priority: string | null;
    channel: string | null;
    visibility: string | null;
    date_range: boolean;
  };
}

// Preset query configurations
interface PresetQuery {
  label: string;
  icon: string;
  priority?: 'High' | 'Medium' | 'Low';
  channel?: 'Display' | 'Radio' | 'Broadcast TV' | 'YouTube' | 'Search' | 'DOOH' | 'Streaming TV' | 'Paid Social' | 'Sport Signage';
  visibility_status?: 'zero' | 'critical' | 'low' | 'fair' | 'good';
  description: string;
}

const PRESET_QUERIES: PresetQuery[] = [
  {
    label: 'High Priority + Zero Visibility',
    icon: 'crisis_alert',
    priority: 'High',
    visibility_status: 'zero',
    description: 'High priority dealers with zero advertising impressions'
  },
  {
    label: 'High Priority + Low YouTube',
    icon: 'warning',
    priority: 'High',
    channel: 'YouTube',
    visibility_status: 'low',
    description: 'High priority dealers with low YouTube visibility'
  },
  {
    label: 'High Priority + Critical Display',
    icon: 'error',
    priority: 'High',
    channel: 'Display',
    visibility_status: 'critical',
    description: 'High priority dealers with critical Display advertising'
  },
  {
    label: 'All Zero Visibility',
    icon: 'visibility_off',
    visibility_status: 'zero',
    description: 'All dealers with zero advertising impressions'
  },
  {
    label: 'Critical Radio Coverage',
    icon: 'radio',
    channel: 'Radio' as const,
    visibility_status: 'critical',
    description: 'Dealers with critically low Radio advertising'
  },
  {
    label: 'Low Search Visibility',
    icon: 'search_off',
    channel: 'Search',
    visibility_status: 'low',
    description: 'Dealers with low Search advertising visibility'
  }
];

interface CoverageAnalysisViewProps {
  hierarchicalScope?: 'corporate' | 'regional' | 'district';
  managerName?: string;
  scopeMessage?: string;
}

const CoverageAnalysisView: React.FC<CoverageAnalysisViewProps> = ({
  hierarchicalScope,
  managerName,
  scopeMessage
}) => {
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1),  // Jan 1 of current year (month is 0-indexed)
    endDate: new Date()
  });

  // Filter states
  const [priority, setPriority] = useState<string>('all');
  const [channel, setChannel] = useState<string>('all');
  const [visibilityStatus, setVisibilityStatus] = useState<string>('all');

  // Data states
  const [data, setData] = useState<AdvancedCoverageData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // UI states
  const [showPresets, setShowPresets] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 25;

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const params: any = {
        start_date: dateRange.startDate.toISOString().split('T')[0],
        end_date: dateRange.endDate.toISOString().split('T')[0]
      };

      if (priority !== 'all') params.priority = priority;
      if (channel !== 'all') params.channel = channel;
      if (visibilityStatus !== 'all') params.visibility_status = visibilityStatus;

      const result = await adminAPI.getAdvancedCoverageAnalysis(params);
      setData(result);
      setCurrentPage(1);
      setShowPresets(false); // Hide presets after first query
    } catch (err: any) {
      console.error('Failed to fetch advanced coverage analysis:', err);
      setError(err.response?.data?.error || err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (start: Date, end: Date) => {
    setDateRange({ startDate: start, endDate: end });
  };

  const handleAnalyze = () => {
    fetchData();
  };

  const handlePresetQuery = (preset: PresetQuery) => {
    setPriority(preset.priority || 'all');
    setChannel(preset.channel || 'all');
    setVisibilityStatus(preset.visibility_status || 'all');

    // Trigger fetch after state updates
    setTimeout(() => {
      fetchData();
    }, 100);
  };

  const handleResetFilters = () => {
    setPriority('all');
    setChannel('all');
    setVisibilityStatus('all');
    setShowPresets(true);
  };

  const exportCSV = () => {
    if (!data) return;

    const isAllChannels = channel === 'all';
    const baseHeaders = ['Dealer ID', 'Dealer Name', 'Spend', 'eCPM', 'Proj. Store Visits (180d)', 'Priority', 'Visibility Status'];
    const impressionHeaders = isAllChannels
      ? [...CHANNEL_COLUMNS, 'Total Impressions']
      : ['Channel', 'Impressions'];
    const csvRows = [
      [...baseHeaders, ...impressionHeaders, 'City', 'State', 'District Manager', 'Regional Manager']
    ];

    data.dealers.forEach((dealer) => {
      const baseFields = [
        dealer.dealer_id,
        dealer.dealer_name,
        dealer.spend != null ? `$${dealer.spend.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : '',
        dealer.ecpm != null ? `$${dealer.ecpm.toFixed(2)}` : '',
        dealer.projected_store_visits != null ? dealer.projected_store_visits.toString() : '',
        dealer.priority_level,
        dealer.visibility_status
      ];
      const impressionFields = isAllChannels
        ? [
            ...CHANNEL_COLUMNS.map(ch => (dealer.channel_visibility?.[ch]?.impressions || 0).toString()),
            dealer.impressions.toString()
          ]
        : [dealer.channel || 'Overall', dealer.impressions.toString()];
      csvRows.push([
        ...baseFields,
        ...impressionFields,
        dealer.city,
        dealer.state,
        dealer.district_manager || 'N/A',
        dealer.regional_manager || 'N/A'
      ]);
    });

    const csv = csvRows.map(row =>
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const timestamp = new Date().toISOString().split('T')[0];
    link.download = `advanced-coverage-analysis-${timestamp}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'zero': return 'status-badge status-zero';
      case 'critical': return 'status-badge status-critical';
      case 'low': return 'status-badge status-low';
      case 'fair': return 'status-badge status-fair';
      case 'good': return 'status-badge status-good';
      default: return 'status-badge';
    }
  };

  const getPriorityBadgeClass = (priority: string): string => {
    switch (priority) {
      case 'High': return 'priority-badge priority-high';
      case 'Medium': return 'priority-badge priority-medium';
      case 'Low': return 'priority-badge priority-low';
      default: return 'priority-badge';
    }
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  if (loading && !data) {
    return (
      <div className="coverage-loading">
        <div className="loading-spinner"></div>
        <p>Analyzing advanced coverage metrics...</p>
      </div>
    );
  }

  return (
    <div className="coverage-analysis-view">
      <div className="analysis-header">
        <h2>Advanced Coverage Analysis</h2>
        <p>Filter dealers by priority level, channel visibility, and advertising performance</p>
      </div>

      {/* Hierarchical Scope Message */}
      {scopeMessage && (
        <div className="scope-message-banner">
          <span className="material-icons">info</span>
          <span>{scopeMessage}</span>
        </div>
      )}

      {/* Preset Query Buttons */}
      {showPresets && (
        <div className="preset-queries">
          <h3>Quick Queries</h3>
          <div className="preset-grid">
            {PRESET_QUERIES.map((preset, index) => (
              <button
                key={index}
                className="preset-button"
                onClick={() => handlePresetQuery(preset)}
                disabled={loading}
              >
                <i className="material-icons">{preset.icon}</i>
                <div className="preset-content">
                  <strong>{preset.label}</strong>
                  <span>{preset.description}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Advanced Filters */}
      <div className="advanced-filters">
        <div className="filters-row">
          <div className="filter-group">
            <label>Date Range:</label>
            <DateSelector
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onDateChange={handleDateChange}
            />
          </div>

          <div className="filter-group">
            <label>Priority Level:</label>
            <select value={priority} onChange={(e) => setPriority(e.target.value)} className="filter-select">
              <option value="all">All Priorities</option>
              <option value="High">High Priority</option>
              <option value="Medium">Medium Priority</option>
              <option value="Low">Low Priority</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Channel:</label>
            <select value={channel} onChange={(e) => setChannel(e.target.value)} className="filter-select">
              <option value="all">All Channels (Overall)</option>
              <option value="Display">Display</option>
              <option value="DOOH">DOOH</option>
              <option value="Streaming TV">Streaming TV</option>
              <option value="Streaming Audio">Streaming Audio</option>
              <option value="Broadcast TV">Broadcast TV</option>
              <option value="Radio">Radio</option>
              <option value="Search">Search</option>
              <option value="Sport Signage">Sport Signage</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Visibility Status:</label>
            <select value={visibilityStatus} onChange={(e) => setVisibilityStatus(e.target.value)} className="filter-select">
              <option value="all">All Status Levels</option>
              <option value="zero">Zero Visibility</option>
              <option value="critical">Critical</option>
              <option value="low">Low</option>
              <option value="fair">Fair</option>
              <option value="good">Good</option>
            </select>
          </div>
        </div>

        <div className="filter-actions">
          <button onClick={handleAnalyze} disabled={loading} className="analyze-button">
            {loading ? (
              <>
                <span className="spinner"></span>
                <span>Analyzing...</span>
              </>
            ) : (
              <>
                <i className="material-icons">analytics</i>
                <span>Run Analysis</span>
              </>
            )}
          </button>

          <button onClick={handleResetFilters} className="reset-button">
            <i className="material-icons">restart_alt</i>
            <span>Reset Filters</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <i className="material-icons">error</i>
          <span>{error}</span>
        </div>
      )}

      {data && !error && (
        <>
          {/* Summary Statistics */}
          <div className="summary-section">
            <h3>Summary Statistics</h3>
            <div className="summary-grid">
              <div className="stat-card total">
                <i className="material-icons">store</i>
                <div className="stat-content">
                  <h4>Total Matching</h4>
                  <p className="big-number">{formatNumber(data.summary.total_matching)}</p>
                </div>
              </div>

              <div className="stat-card priority-high">
                <i className="material-icons">priority_high</i>
                <div className="stat-content">
                  <h4>High Priority</h4>
                  <p className="big-number">{formatNumber(data.summary.by_priority.High)}</p>
                </div>
              </div>

              <div className="stat-card priority-medium">
                <i className="material-icons">drag_handle</i>
                <div className="stat-content">
                  <h4>Medium Priority</h4>
                  <p className="big-number">{formatNumber(data.summary.by_priority.Medium)}</p>
                </div>
              </div>

              <div className="stat-card priority-low">
                <i className="material-icons">low_priority</i>
                <div className="stat-content">
                  <h4>Low Priority</h4>
                  <p className="big-number">{formatNumber(data.summary.by_priority.Low)}</p>
                </div>
              </div>
            </div>

            <div className="visibility-breakdown">
              <h4>Visibility Breakdown</h4>
              <div className="visibility-stats">
                <div className="vis-stat zero">
                  <span className="vis-label">Zero:</span>
                  <span className="vis-count">{formatNumber(data.summary.by_visibility.zero)}</span>
                </div>
                <div className="vis-stat critical">
                  <span className="vis-label">Critical:</span>
                  <span className="vis-count">{formatNumber(data.summary.by_visibility.critical)}</span>
                </div>
                <div className="vis-stat low">
                  <span className="vis-label">Low:</span>
                  <span className="vis-count">{formatNumber(data.summary.by_visibility.low)}</span>
                </div>
                <div className="vis-stat fair">
                  <span className="vis-label">Fair:</span>
                  <span className="vis-count">{formatNumber(data.summary.by_visibility.fair)}</span>
                </div>
                <div className="vis-stat good">
                  <span className="vis-label">Good:</span>
                  <span className="vis-count">{formatNumber(data.summary.by_visibility.good)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Results Table */}
          {data.dealers.length > 0 ? (
            <>
              <div className="table-header">
                <h3>Matching Dealers ({formatNumber(data.dealers.length)})</h3>
                <button onClick={exportCSV} className="export-button">
                  <i className="material-icons">download</i>
                  <span>Export to CSV</span>
                </button>
              </div>

              <div className="dealers-table-container">
                <table className="dealers-table">
                  <thead>
                    <tr>
                      <th>Dealer Name</th>
                      <th>Dealer ID</th>
                      <th>Spend</th>
                      <th>eCPM</th>
                      <th>Proj. Visits</th>
                      <th>Priority</th>
                      <th>Visibility</th>
                      {data.filters_applied.channel ? (
                        <>
                          <th>Channel</th>
                          <th>Impressions</th>
                        </>
                      ) : (
                        <>
                          {CHANNEL_COLUMNS.map(ch => <th key={ch}>{ch}</th>)}
                          <th>Total</th>
                        </>
                      )}
                      <th>Location</th>
                      <th>District Manager</th>
                      <th>Regional Manager</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.dealers.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((dealer) => (
                      <tr key={dealer.dealer_id}>
                        <td className="dealer-name">{dealer.dealer_name}</td>
                        <td className="dealer-id">{dealer.dealer_id}</td>
                        <td className="number-cell">
                          {dealer.spend != null
                            ? `$${dealer.spend.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                            : '—'}
                        </td>
                        <td className="number-cell">
                          {dealer.ecpm != null
                            ? `$${dealer.ecpm.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : '—'}
                        </td>
                        <td className="number-cell">
                          {dealer.projected_store_visits != null
                            ? dealer.projected_store_visits.toLocaleString('en-US')
                            : '—'}
                          {dealer.fta_dampened && (
                            <i
                              className="material-icons fta-dampen-icon"
                              title={`Dampened — raw signal was ${dealer.fta_raw_projected?.toLocaleString('en-US')} projected visits. Flags: ${dealer.fta_anomaly_flags?.join(', ')}`}
                            >warning</i>
                          )}
                          {!dealer.fta_dampened && dealer.fta_anomaly_flags && dealer.fta_anomaly_flags.length > 0 && (
                            <i
                              className="material-icons fta-flag-icon"
                              title={`FTA signal quality flags: ${dealer.fta_anomaly_flags.join(', ')}`}
                            >info</i>
                          )}
                        </td>
                        <td>
                          <span className={getPriorityBadgeClass(dealer.priority_level)}>
                            {dealer.priority_level}
                          </span>
                        </td>
                        <td>
                          <span className={getStatusBadgeClass(dealer.visibility_status)}>
                            {dealer.visibility_status.charAt(0).toUpperCase() + dealer.visibility_status.slice(1)}
                          </span>
                        </td>
                        {data.filters_applied.channel ? (
                          <>
                            <td className="channel-cell">{dealer.channel || 'Overall'}</td>
                            <td className="number-cell">{formatNumber(dealer.impressions)}</td>
                          </>
                        ) : (
                          <>
                            {CHANNEL_COLUMNS.map(ch => (
                              <td key={ch} className="number-cell">
                                {formatNumber(dealer.channel_visibility?.[ch]?.impressions || 0)}
                              </td>
                            ))}
                            <td className="number-cell">{formatNumber(dealer.impressions)}</td>
                          </>
                        )}
                        <td className="location">
                          {dealer.city && dealer.state ? `${dealer.city}, ${dealer.state}` : 'N/A'}
                        </td>
                        <td className="manager">{dealer.district_manager || 'N/A'}</td>
                        <td className="manager">{dealer.regional_manager || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {data.dealers.length > PAGE_SIZE && (
                <div className="pagination-controls">
                  <button
                    className="page-btn"
                    onClick={() => setCurrentPage(p => p - 1)}
                    disabled={currentPage === 1}
                  >
                    <i className="material-icons">chevron_left</i>
                  </button>
                  <span className="page-info">
                    Page {currentPage} of {Math.ceil(data.dealers.length / PAGE_SIZE)}
                    <span className="page-count"> ({formatNumber(data.dealers.length)} total)</span>
                  </span>
                  <button
                    className="page-btn"
                    onClick={() => setCurrentPage(p => p + 1)}
                    disabled={currentPage === Math.ceil(data.dealers.length / PAGE_SIZE)}
                  >
                    <i className="material-icons">chevron_right</i>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="no-results-message">
              <i className="material-icons">check_circle</i>
              <h3>No Dealers Match Criteria</h3>
              <p>No dealers found matching the selected filters. Try adjusting your criteria.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CoverageAnalysisView;
